import React from 'react';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { ClientContainer } from '../../providers';
import { ScrollArea } from '../ui/scroll-area';

const ClientsDropdown = ({ selected = [], onChange, options, disabled }) => {
  const [open, setOpen] = React.useState(false);
  const [search, setSearch] = React.useState('');
  const { state: clientState } = ClientContainer.useContainer();

  const clientsList = options ?? clientState.clients;

  const filteredList = clientsList.filter(({ name }) =>
    name.toLowerCase().includes(search.toLowerCase())
  );

  // Helper function to normalize ID for comparison
  const normalizeId = id => id?.toString();

  // Helper function to check if an ID is selected
  const isIdSelected = id => {
    const normalizedId = normalizeId(id);
    return selected.some(
      selectedId => normalizeId(selectedId) === normalizedId
    );
  };

  const selectedClients = clientsList.filter(client => isIdSelected(client.id));

  const isAllSelected =
    clientsList.length === selected.length && filteredList.length !== 0;

  const handleSelect = clientId => {
    if (clientId === 'all') {
      if (isAllSelected) {
        onChange([]);
      } else {
        const firstSelectedType = selected[0] ? typeof selected[0] : 'number';
        const allIds = clientsList.map(client =>
          firstSelectedType === 'string'
            ? client.id.toString()
            : Number(client.id)
        );
        onChange(allIds);
      }
      return;
    }

    const normalizedClientId = normalizeId(clientId);
    const isCurrentlySelected = isIdSelected(clientId);

    let newSelected;
    if (isCurrentlySelected) {
      newSelected = selected.filter(
        id => normalizeId(id) !== normalizedClientId
      );
    } else {
      const convertedId =
        selected.length > 0 && typeof selected[0] === 'string'
          ? clientId.toString()
          : Number(clientId);
      newSelected = [...selected, convertedId];
    }

    onChange(newSelected);
  };

  const renderTriggerContent = () => {
    if (isAllSelected) {
      return <span className="text-sm">All workspaces</span>;
    }

    if (selectedClients.length > 0) {
      return (
        <div className="flex items-center gap-2">
          <div className="flex -space-x-2">
            {selectedClients
              .slice(0, 3)
              .map(client =>
                client.logo ? (
                  <img
                    key={client.id}
                    src={client.logo}
                    alt={client.name}
                    className="h-6 w-6 rounded-full border-2 border-white"
                  />
                ) : (
                  <span className="h-6 w-6 bg-primary-100 rounded-full">
                    {client.name.slice(0, 1)}
                  </span>
                )
              )}
          </div>
          {selectedClients.length > 3 && (
            <span className="text-sm text-gray-600">
              +{selectedClients.length - 3} more
            </span>
          )}
          <span className="text-sm ml-2">
            {selectedClients.length}{' '}
            {selectedClients.length === 1 ? 'workspace' : 'workspaces'}
          </span>
        </div>
      );
    }

    return <span className="text-sm text-gray-600">Add workspace</span>;
  };

  return (
    <Popover open={open} onOpenChange={setOpen} modal>
      <PopoverTrigger asChild>
        <Button
          intent="secondary"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between focus:!outline-none focus:!ring-4 focus:!ring-offset-0 focus:!ring-[hsla(var(--primary),0.1)] focus:!border-primary-400"
          disabled={disabled}
        >
          <div className="flex items-center gap-2">
            {renderTriggerContent()}
          </div>
          <i className="ti ti-chevron-down h-4 w-4 opacity-50 ml-2" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start">
        <Command>
          <CommandInput
            placeholder="Search workspace"
            value={search}
            onValueChange={setSearch}
            className="h-9"
          />

          <ScrollArea>
            <CommandList>
              <CommandEmpty className="py-6 text-center text-sm">
                No workspaces found
              </CommandEmpty>
              <CommandGroup>
                {filteredList && filteredList.length > 0 && (
                  <CommandItem
                    key="all"
                    onSelect={() => handleSelect('all')}
                    className="group gap-2 px-2 py-1.5"
                  >
                    <div className="flex h-5 w-5 items-center justify-center rounded-sm border border-primary">
                      {isAllSelected && <i className="ti ti-check h-4 w-4" />}
                    </div>
                    <span>All workspaces</span>
                  </CommandItem>
                )}

                {filteredList.map(client => {
                  const isActive = isIdSelected(client.id);

                  return (
                    <CommandItem
                      key={client.id}
                      onSelect={() => handleSelect(client.id)}
                      className="px-2 py-1.5"
                    >
                      <div className="flex h-5 w-5 items-center justify-center rounded-sm border border-primary">
                        {isActive && <i className="ti ti-check h-4 w-4" />}
                      </div>

                      {client.logo ? (
                        <img
                          alt="business logo"
                          className="h-6 w-6 rounded-full"
                          src={client.logo}
                        />
                      ) : (
                        <span className="h-6 w-6 bg-primary-100 rounded-full text-center">
                          {client.name.slice(0, 1)}
                        </span>
                      )}
                      <div className="flex items-center gap-2">
                        <span>{client.name}</span>
                      </div>
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            </CommandList>
          </ScrollArea>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export default ClientsDropdown;
